import React, { useState, useEffect, useMemo } from 'react';
import { shipmentService } from '@/services/shipmentService';
import { configService } from '@/services/configService';
import { calculatePrice } from '@/utils/pricing';
import { useAuth } from '@/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBox, faUser, faTruck, faMoneyBillWave, faPlus, faWeightHanging, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const AgentBookShipment = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [shipmentDetails, setShipmentDetails] = useState({
    senderName: '',
    senderNumber: '',
    senderEmail: '',
    senderAddress: '',
    senderLocation: '', // Can be area, state, or country
    receiverName: '',
    receiverNumber: '',
    receiverEmail: '',
    receiverAddress: '',
    receiverLocation: '', // Can be area, state, or country
    deliveryType: 'doorstep',
    serviceType: 'interstate',
    price: '',
    weight: '1',
    numberOfItems: '1',
    userRole: 'user',
    contentList: '',
    paymentType: 'payNow',
    paymentMethod: 'cash',
    codAmount: '',
    internationalSubtype: '',
    customPrice: '',
    isNegotiated: false,
  });

  const [pricingConfig, setPricingConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [priceCalculating, setPriceCalculating] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!shipmentDetails.senderName) errors.senderName = 'Sender name is required';
    if (!shipmentDetails.senderNumber) errors.senderNumber = 'Sender phone is required';
    if (!shipmentDetails.senderLocation) errors.senderLocation = 'Sender location is required';
    if (!shipmentDetails.senderAddress) errors.senderAddress = 'Pickup address is required';

    if (!shipmentDetails.receiverName) errors.receiverName = 'Receiver name is required';
    if (!shipmentDetails.receiverNumber) errors.receiverNumber = 'Receiver phone is required';
    if (!shipmentDetails.receiverLocation) errors.receiverLocation = 'Receiver location is required';

    // For local and international, address is mandatory. For others, it depends on deliveryType.
    const isLocalOrInternational = ['local', 'international'].includes(shipmentDetails.serviceType);
    if ((isLocalOrInternational || shipmentDetails.deliveryType === 'doorstep') && !shipmentDetails.receiverAddress) {
      // For international cargo, address might be optional if they pick up from hub? 
      // But usually international requires address. Let's stick with the User dashboard logic.
      if (shipmentDetails.serviceType === 'international' && shipmentDetails.internationalSubtype === 'cargo') {
        // Cargo might not require full address if they use the hub, but usually we want one.
      } else {
        errors.receiverAddress = isLocalOrInternational
          ? 'Drop-off address is required'
          : 'Receiver address is required for doorstep delivery';
      }
    }

    if (shipmentDetails.serviceType === 'international' && !shipmentDetails.internationalSubtype) {
      errors.internationalSubtype = 'Please select Express or Cargo';
    }

    if (!shipmentDetails.weight || parseFloat(shipmentDetails.weight) <= 0) {
      errors.weight = 'Valid weight is required';
    } else {
      const weight = parseFloat(shipmentDetails.weight);
      if (shipmentDetails.serviceType === 'international' && shipmentDetails.internationalSubtype === 'cargo' && weight < 10) {
        errors.weight = 'Minimum weight for Cargo is 10kg';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGeneratePrice = () => {
    setError(null);
    if (!validateForm()) {
      setError('Please fix the highlighted errors before generating price.');
      return;
    }

    if (!pricingConfig) {
      setError('Pricing configuration not loaded. Please check your internet connection and refresh.');
      return;
    }

    setPriceCalculating(true);
    try {
      const weight = parseFloat(shipmentDetails.weight) || 0;
      if (weight <= 0) {
        setError('Please enter a valid weight greater than 0.');
        return;
      }

      // For interstate & haulage, the pricing engine requires senderState === 'Lagos'
      // because agents always dispatch from the Lagos hub.
      const isInterstate = ['interstate', 'haulage'].includes(shipmentDetails.serviceType);
      const effectiveSenderState = isInterstate ? 'Lagos' : shipmentDetails.senderLocation;

      const calculated = calculatePrice({
        serviceType: shipmentDetails.serviceType,
        senderState: effectiveSenderState,
        receiverState: shipmentDetails.receiverLocation,
        country: shipmentDetails.receiverLocation, // used for international
        weight,
        userRole: shipmentDetails.userRole,
        subtype: shipmentDetails.serviceType === 'international' ? shipmentDetails.internationalSubtype : 'express',
        deliveryType: shipmentDetails.deliveryType
      }, pricingConfig);

      if (calculated) {
        setShipmentDetails(prev => ({ ...prev, price: String(calculated) }));
        setSuccess('Price generated successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        // Provide more context for failure
        let reason = 'Check if the destination and weight are supported.';
        if (shipmentDetails.serviceType === 'international' && !shipmentDetails.receiverLocation) {
          reason = 'International shipments require a destination country.';
        }
        setError(`Could not calculate price. ${reason}`);
      }
    } catch (e) {
      console.error('Pricing Error:', e);
      setError('Error calculating price. Please ensure all fields are valid.');
    } finally {
      setPriceCalculating(false);
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const resp = await configService.getPricingConfig();
        if (resp?.config) setPricingConfig(resp.config);
      } catch (e) {
        console.error('Failed to fetch pricing config', e);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const lagosAreas = useMemo(() => {
    if (pricingConfig) {
      // Collect all areas from all zones
      const zones = pricingConfig.userRole === 'ecommerce'
        ? (pricingConfig.ecommerceLagosZones || [])
        : (pricingConfig.lagosZones || []);

      const areas = zones.flatMap(z => z.areas || []);
      if (areas.length > 0) return Array.from(new Set(areas)).sort();
    }
    return [
      'Ikeja', 'Surulere', 'Lagos Island', 'Ikoyi', 'Victoria Island', 'Lekki Phase 1', 'Lekki Phase 2',
      'Ajah', 'Sangotedo', 'Iyana Ipaja', 'Egbeda', 'Agege', 'Alimosho', 'Maryland', 'Gbagada',
      'Oshodi', 'Anthony', 'Ejigbo', 'Isolo', 'Iyana Itire', 'Alausa', 'Oregun', 'Yaba', 'Mushin'
    ].sort();
  }, [pricingConfig]);

  const nigerianStatesList = useMemo(() => {
    // If pricingConfig has interstate zones, use those, otherwise use defaults
    let states = [];
    if (pricingConfig && pricingConfig.interStateZones) {
      states = Object.values(pricingConfig.interStateZones).flat();
    }
    if (states.length === 0) {
      // Final fallback to the exact list of supported cities/states
      states = [
        'Abeokuta', 'Ado Ekiti', 'Osogbo', 'Ibadan', 'Ilorin', 'Akure', // Zone 1
        'Aba', 'Owerri', 'Enugu', 'Onitsha', 'Awka', // Zone 2
        'Asaba', 'Warri', 'Benin', // Zone 3
        'Abuja', 'Uyo', 'Port Harcourt', 'Bayelsa', // Zone 4
        'Kano', 'Kaduna', 'Makurdi', 'Minna', 'Bauchi' // Zone 5
      ];
    }
    // Always ensure Lagos is included (it's the agent hub)
    if (!states.some(s => s.toLowerCase() === 'lagos')) {
      states.push('Lagos');
    }
    return Array.from(new Set(states)).sort();
  }, [pricingConfig]);

  const countries = [
    'United States', 'United Kingdom', 'Canada', 'China', 'Germany', 'France',
    'Italy', 'Spain', 'Netherlands', 'India', 'South Africa', 'United Arab Emirates',
    'Saudi Arabia', 'Malaysia', 'Singapore', 'Australia', 'Brazil', 'Ghana',
    'Kenya', 'Egypt', 'Turkey', 'Japan', 'South Korea', 'Mexico', 'Nigeria', 'Others'
  ].sort();

  // For international sender, provide a list with Nigeria first as origin
  const senderCountries = ['Nigeria', ...countries.filter(c => c !== 'Nigeria')];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShipmentDetails((prev) => {
      const next = { ...prev, [name]: value };

      // Clear internationalSubtype if serviceType is no longer international
      if (name === 'serviceType' && value !== 'international') {
        next.internationalSubtype = '';
      }

      return next;
    });
    // Clear error for the field being changed
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});
    setSuccess(null);

    if (!validateForm()) {
      setError('Please fix the highlighted errors before submitting.');
      setLoading(false);
      return;
    }

    if (!shipmentDetails.isNegotiated && (!shipmentDetails.price || parseFloat(shipmentDetails.price) <= 0)) {
      setError('Please generate a valid price before creating the shipment.');
      setLoading(false);
      return;
    }

    if (shipmentDetails.isNegotiated && (!shipmentDetails.customPrice || parseFloat(shipmentDetails.customPrice) <= 0)) {
      setError('Please enter a valid negotiated price.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const finalPrice = shipmentDetails.isNegotiated ? shipmentDetails.customPrice : shipmentDetails.price;
      const finalCodAmount = (shipmentDetails.paymentType === 'cashOnDelivery' && !shipmentDetails.codAmount)
        ? finalPrice
        : shipmentDetails.codAmount;

      const data = {
        ...shipmentDetails,
        price: finalPrice,
        customPrice: shipmentDetails.isNegotiated ? shipmentDetails.customPrice : undefined,
        originalPrice: shipmentDetails.isNegotiated ? shipmentDetails.price : undefined,
        codAmount: finalCodAmount,
        paymentMethod: shipmentDetails.paymentType === 'payNow' ? shipmentDetails.paymentMethod : undefined,
        agentId: currentUser?.id || currentUser?._id
      };
      const result = await shipmentService.createAgentShipment(data, token);
      setSuccess(`Shipment created successfully! Tracking ID: ${result.trackingCode}`);
      setShipmentDetails({
        senderName: '',
        senderNumber: '',
        senderEmail: '',
        senderAddress: '',
        senderLocation: '',
        receiverName: '',
        receiverNumber: '',
        receiverEmail: '',
        receiverAddress: '',
        receiverLocation: '',
        deliveryType: 'doorstep',
        serviceType: 'interstate',
        price: '',
        weight: '1',
        numberOfItems: '1',
        userRole: 'user',
        contentList: '',
        paymentType: 'payNow',
        paymentMethod: 'cash',
        codAmount: '',
        customPrice: '',
        isNegotiated: false,
      });
      setFieldErrors({});
    } catch (err) {
      const msg = err.response?.data?.msg || 'Failed to create shipment.';
      const details = err.response?.data?.details || err.response?.data?.error || '';
      setError(details ? `${msg} (${details})` : msg);
    } finally {
      setLoading(false);
    }
  };

  const getLocationList = (isSender = false) => {
    switch (shipmentDetails.serviceType) {
      case 'local': return lagosAreas;
      case 'interstate': return nigerianStatesList;
      case 'international': return isSender ? senderCountries : countries;
      case 'haulage': return nigerianStatesList; // Default to states for haulage
      default: return [];
    }
  };

  const locationLabel = () => {
    switch (shipmentDetails.serviceType) {
      case 'local': return 'Location Type';
      case 'interstate': return 'State in Nigeria';
      case 'international': return 'Country';
      case 'haulage': return 'State';
      default: return 'Location';
    }
  };

  const isOutsideLagos = useMemo(() => {
    if (!shipmentDetails.senderLocation) return false;
    if (shipmentDetails.serviceType === 'local') return false; // Always Lagos areas
    if (shipmentDetails.serviceType === 'international') return false; // Usually handled from Lagos hub

    // For interstate and haulage, senderLocation is a state
    return shipmentDetails.senderLocation !== 'Lagos';
  }, [shipmentDetails.senderLocation, shipmentDetails.serviceType]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-gray-600 hover:text-red-600 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Dashboard
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-black via-neutral-800 to-red-600 p-6 text-white">
          <h1 className="text-2xl font-bold flex items-center">
            <FontAwesomeIcon icon={faPlus} className="mr-3" />
            Book a New Shipment
          </h1>
          <p className="text-white/80 text-sm mt-1">Register a new shipment and generate tracking ID instantly.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* User Role & Service Type Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">User Account Type</label>
              <select
                name="userRole"
                value={shipmentDetails.userRole}
                onChange={handleChange}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm bg-white p-2.5"
              >
                <option value="user">Standard User</option>
                <option value="ecommerce">Ecommerce Partner</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Service Type</label>
              <select
                name="serviceType"
                value={shipmentDetails.serviceType}
                onChange={handleChange}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm bg-white p-2.5"
              >
                <option value="interstate">Interstate Delivery</option>
                <option value="local">Local Delivery (Lagos)</option>
                <option value="international">International Shipping</option>
                <option value="haulage">Haulage & Logistics</option>
              </select>
            </div>
          </div>

          {/* International Subtype Selection */}
          {shipmentDetails.serviceType === 'international' && (
            <div className="p-6 bg-gray-50 rounded-xl border border-gray-100 animate-in fade-in slide-in-from-top-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Select International Service Subtype</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setShipmentDetails(prev => ({ ...prev, internationalSubtype: 'express' }))}
                  className={`flex items-center p-4 rounded-xl border-2 transition-all ${shipmentDetails.internationalSubtype === 'express'
                    ? 'border-red-600 bg-white shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${shipmentDetails.internationalSubtype === 'express' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                    <FontAwesomeIcon icon={faBox} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-sm text-gray-800">Express Shipping</p>
                    <p className="text-[10px] text-gray-500">Fast door-to-door delivery</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setShipmentDetails(prev => ({ ...prev, internationalSubtype: 'cargo' }))}
                  className={`flex items-center p-4 rounded-xl border-2 transition-all ${shipmentDetails.internationalSubtype === 'cargo'
                    ? 'border-red-600 bg-white shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${shipmentDetails.internationalSubtype === 'cargo' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                    <FontAwesomeIcon icon={faTruck} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-sm text-gray-800">Cargo Shipping</p>
                    <p className="text-[10px] text-gray-500">Economy (Min. 10kg)</p>
                  </div>
                </button>
              </div>
              {fieldErrors.internationalSubtype && <p className="text-red-500 text-xs mt-2">{fieldErrors.internationalSubtype}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Sender Section */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-800 flex items-center pb-2 border-b">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                Sender Details
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="senderName"
                    value={shipmentDetails.senderName}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.senderName ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                  {fieldErrors.senderName && <p className="text-red-500 text-xs mt-1">{fieldErrors.senderName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="text"
                    name="senderNumber"
                    value={shipmentDetails.senderNumber}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.senderNumber ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                  {fieldErrors.senderNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.senderNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="senderEmail"
                    value={shipmentDetails.senderEmail}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{locationLabel()} (Sender)</label>
                  {shipmentDetails.serviceType === 'local' ? (
                    <select
                      name="senderLocation"
                      value={shipmentDetails.senderLocation}
                      onChange={handleChange}
                      className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.senderLocation ? 'border-red-500 bg-red-50' : ''}`}
                      required
                    >
                      <option value="">Select Location Type</option>
                      <option value="Mainland">Mainland</option>
                      <option value="Island">Island</option>
                      <option value="Outskirt">Outskirt</option>
                    </select>
                  ) : (
                    <>
                      <input
                        list="senderLocationList"
                        name="senderLocation"
                        value={shipmentDetails.senderLocation}
                        onChange={handleChange}
                        className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.senderLocation ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder={`Type or select ${locationLabel().toLowerCase()}`}
                        required
                      />
                      <datalist id="senderLocationList">
                        {getLocationList(true).map((loc) => (
                          <option key={loc} value={loc} />
                        ))}
                      </datalist>
                    </>
                  )}
                  {fieldErrors.senderLocation && <p className="text-red-500 text-xs mt-1">{fieldErrors.senderLocation}</p>}
                  {isOutsideLagos && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
                      <p className="text-xs text-blue-800 font-medium">
                        Note: Pricing is calculated from the Lagos hub. For pickup outside Lagos, contact <span className="font-bold">07046674299</span> for arrangement.
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
                  <textarea
                    name="senderAddress"
                    value={shipmentDetails.senderAddress}
                    onChange={handleChange}
                    rows="3"
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.senderAddress ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  ></textarea>
                  {fieldErrors.senderAddress && <p className="text-red-500 text-xs mt-1">{fieldErrors.senderAddress}</p>}
                </div>
              </div>
            </div>

            {/* Receiver Section */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-800 flex items-center pb-2 border-b">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                Receiver Details
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    name="receiverName"
                    value={shipmentDetails.receiverName}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.receiverName ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                  {fieldErrors.receiverName && <p className="text-red-500 text-xs mt-1">{fieldErrors.receiverName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="text"
                    name="receiverNumber"
                    value={shipmentDetails.receiverNumber}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.receiverNumber ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                  {fieldErrors.receiverNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.receiverNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="receiverEmail"
                    value={shipmentDetails.receiverEmail}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{locationLabel()} (Receiver)</label>
                  {shipmentDetails.serviceType === 'local' ? (
                    <select
                      name="receiverLocation"
                      value={shipmentDetails.receiverLocation}
                      onChange={handleChange}
                      className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.receiverLocation ? 'border-red-500 bg-red-50' : ''}`}
                      required
                    >
                      <option value="">Select Location Type</option>
                      <option value="Mainland">Mainland</option>
                      <option value="Island">Island</option>
                      <option value="Outskirt">Outskirt</option>
                    </select>
                  ) : (
                    <>
                      <input
                        list="receiverLocationList"
                        name="receiverLocation"
                        value={shipmentDetails.receiverLocation}
                        onChange={handleChange}
                        className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.receiverLocation ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder={`Type or select ${locationLabel().toLowerCase()}`}
                        required
                      />
                      <datalist id="receiverLocationList">
                        {getLocationList().map((loc) => (
                          <option key={loc} value={loc} />
                        ))}
                      </datalist>
                    </>
                  )}
                  {fieldErrors.receiverLocation && <p className="text-red-500 text-xs mt-1">{fieldErrors.receiverLocation}</p>}
                </div>

                {!['local', 'international'].includes(shipmentDetails.serviceType) && (
                  <div className="pt-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Delivery Option</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="doorstep"
                          checked={shipmentDetails.deliveryType === 'doorstep'}
                          onChange={handleChange}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Doorstep Delivery</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="pickup"
                          checked={shipmentDetails.deliveryType === 'pickup'}
                          onChange={handleChange}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Hub Pickup</span>
                      </label>
                    </div>
                  </div>
                )}

                {(['local', 'international'].includes(shipmentDetails.serviceType) || shipmentDetails.deliveryType === 'doorstep') && (
                  <div className="animate-in fade-in slide-in-from-top-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {shipmentDetails.serviceType === 'local' ? 'Drop-off Address or Nearest Bus Stop' : 'Drop-off Address'}
                    </label>
                    <textarea
                      name="receiverAddress"
                      value={shipmentDetails.receiverAddress}
                      onChange={handleChange}
                      rows="3"
                      className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.receiverAddress ? 'border-red-500 bg-red-50' : ''}`}
                      placeholder={shipmentDetails.serviceType === 'local' ? "Enter full address or nearest bus stop" : "Enter the full delivery address"}
                      required={['local', 'international'].includes(shipmentDetails.serviceType) || shipmentDetails.deliveryType === 'doorstep'}
                    ></textarea>
                    {fieldErrors.receiverAddress && <p className="text-red-500 text-xs mt-1">{fieldErrors.receiverAddress}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shipment Content</label>
                  <textarea
                    name="contentList"
                    value={shipmentDetails.contentList}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Enter what's inside the package"
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border"
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <FontAwesomeIcon icon={faWeightHanging} className="mr-2 text-red-600" />
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      name="weight"
                      value={shipmentDetails.weight}
                      onChange={handleChange}
                      step="0.1"
                      min="0.1"
                      className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border ${fieldErrors.weight ? 'border-red-500 bg-red-50' : ''}`}
                      required
                    />
                    {fieldErrors.weight && <p className="text-red-500 text-xs mt-1">{fieldErrors.weight}</p>}

                    {/* International Info Box */}
                    {shipmentDetails.serviceType === 'international' && shipmentDetails.internationalSubtype && (
                      <div className="mt-4 p-4 rounded-xl border-2 transition-all bg-gray-50 border-gray-200 animate-in fade-in slide-in-from-top-1">
                        <h4 className="font-bold text-[10px] text-gray-500 uppercase tracking-widest mb-2 flex items-center">
                          <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-500" />
                          {shipmentDetails.internationalSubtype === 'express' ? 'Express' : 'Cargo'} Details
                        </h4>
                        <div className="space-y-1">
                          {shipmentDetails.internationalSubtype === 'cargo' ? (
                            <p className="text-xs text-gray-600 font-medium italic">Economy cargo service for heavier shipments. Minimum weight: 10kg.</p>
                          ) : (
                            <p className="text-xs text-gray-600">Fast international door-to-door delivery. Estimated time: 3-5 business days.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Items</label>
                    <input
                      type="number"
                      name="numberOfItems"
                      value={shipmentDetails.numberOfItems || '1'}
                      onChange={handleChange}
                      min="1"
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Payment Category</label>
                  <div className="flex gap-4 items-center">
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="paymentType"
                        value="payNow"
                        checked={shipmentDetails.paymentType === 'payNow'}
                        onChange={handleChange}
                        className="text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Prepaid</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="paymentType"
                        value="cashOnDelivery"
                        checked={shipmentDetails.paymentType === 'cashOnDelivery'}
                        onChange={handleChange}
                        className="text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-700">COD (To Collect)</span>
                    </label>
                  </div>
                </div>

                {shipmentDetails.paymentType === 'payNow' && (
                  <div className="pt-2 animate-in fade-in slide-in-from-top-1">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Payment Method (Received By Agent)</label>
                    <div className="flex flex-wrap gap-3">
                      <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border transition-all ${shipmentDetails.paymentMethod === 'transfer' ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="transfer"
                          checked={shipmentDetails.paymentMethod === 'transfer'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-bold uppercase tracking-tight">Transfer</span>
                      </label>
                      <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border transition-all ${shipmentDetails.paymentMethod === 'pos' ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="pos"
                          checked={shipmentDetails.paymentMethod === 'pos'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-bold uppercase tracking-tight">POS</span>
                      </label>
                      <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border transition-all ${shipmentDetails.paymentMethod === 'cash' ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cash"
                          checked={shipmentDetails.paymentMethod === 'cash'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-bold uppercase tracking-tight">Cash</span>
                      </label>
                    </div>
                  </div>
                )}

                {shipmentDetails.paymentType === 'cashOnDelivery' && (
                  <div className="animate-in fade-in slide-in-from-top-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">COD Amount (₦)</label>
                    <input
                      type="number"
                      name="codAmount"
                      value={shipmentDetails.codAmount}
                      onChange={handleChange}
                      placeholder="Enter amount to collect on delivery"
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border"
                      required={shipmentDetails.paymentType === 'cashOnDelivery'}
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to use the agreed shipment price (₦{shipmentDetails.price || '0.00'})</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-black text-white p-6 rounded-xl flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Automated Pricing</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-1">
                    <p className="text-sm text-gray-300">
                      {shipmentDetails.receiverLocation
                        ? `Based on ${shipmentDetails.serviceType} rates`
                        : 'Fill in details to calculate price'}
                    </p>
                    <button
                      type="button"
                      onClick={handleGeneratePrice}
                      disabled={priceCalculating}
                      className={`bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-white/20 transition-all flex items-center`}
                    >
                      {priceCalculating ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
                      )}
                      Generate Price
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-64 text-center md:text-right">
                <div className="text-3xl font-black text-red-500 mb-1">
                  ₦{shipmentDetails.price ? Number(shipmentDetails.price).toLocaleString() : '0.00'}
                </div>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Total Shipping Cost</p>
              </div>
            </div>

            {/* Negotiation Section */}
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isNegotiated"
                    name="isNegotiated"
                    checked={shipmentDetails.isNegotiated}
                    onChange={(e) => setShipmentDetails(prev => ({ ...prev, isNegotiated: e.target.checked }))}
                    className="w-4 h-4 text-red-600 bg-gray-800 border-gray-700 rounded focus:ring-red-500 focus:ring-offset-gray-900"
                  />
                  <label htmlFor="isNegotiated" className="text-sm font-bold text-gray-300 cursor-pointer">
                    Enable Price Negotiation
                  </label>
                </div>
                {shipmentDetails.isNegotiated && (
                  <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                    Negotiation Active
                  </span>
                )}
              </div>

              {shipmentDetails.isNegotiated && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Negotiated Price (₦)</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">₦</span>
                    <input
                      type="number"
                      name="customPrice"
                      value={shipmentDetails.customPrice}
                      onChange={handleChange}
                      placeholder="Enter the agreed price"
                      className="block w-full bg-neutral-900 border-neutral-700 text-white rounded-lg pl-8 pr-4 py-2.5 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 italic">
                    * This price will override the automated calculation and be marked as negotiated.
                  </p>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-center text-red-700">
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded flex flex-col gap-2 text-green-700">
              <p className="text-sm font-bold">{success}</p>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/dashboard/agent/shipments')}
                  className="text-xs font-bold underline hover:text-green-800"
                >
                  View All Shipments
                </button>
                <button
                  onClick={() => setSuccess(null)}
                  className="text-xs font-bold underline hover:text-green-800"
                >
                  Book Another
                </button>
              </div>
            </div>
          )}

          <div className="pt-4">
            <button
              type="submit"
              className="w-full bg-red-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all shadow-lg hover:shadow-red-500/20 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                  Creating Shipment...
                </span>
              ) : (
                'Create Shipment & Generate Tracking ID'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentBookShipment;
